import { useState } from "react";

import { api, ApiError } from "../lib/api";
import { ConfirmDialog, AlertDialog } from "../components/ConfirmDialog";
import { Pager } from "../components/Pager";
import { useAutoPageSize } from "../lib/useAutoPageSize";

/**
 * 网站维护（仅超管）。Tab 框架，目前只有「清理文件」一个 Tab，后续可扩展更多维护工具。
 */

type TabKey = "cleanup" | "broken";
const TABS: { key: TabKey; label: string }[] = [
  { key: "cleanup", label: "检查失效文件" },
  { key: "broken", label: "检查失效链接" },
];

export function MaintenancePage() {
  const [tab, setTab] = useState<TabKey>("cleanup");
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">网站维护</div>
        </div>
      </div>

      {/* Tab 切换 */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 16 }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              background: "none", border: "none", padding: "8px 16px", cursor: "pointer", fontSize: 14,
              color: tab === t.key ? "var(--gold)" : "var(--mute)",
              borderBottom: tab === t.key ? "2px solid var(--gold)" : "2px solid transparent",
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "cleanup" && <CleanupFilesTab />}
      {tab === "broken" && <BrokenRefsTab />}
    </div>
  );
}

// ============ 检查失效图 Tab（DB 引用但对象存储不存在的文件）============

type BrokenRef = { table: string; id: number | null; field: string; key: string; fixable: boolean; label: string };

function BrokenRefsTab() {
  const [broken, setBroken] = useState<BrokenRef[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const fixableCount = broken?.filter((b) => b.fixable).length ?? 0;

  const scan = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.get<{ broken: BrokenRef[] }>("/api/admin/orphans/broken/scan");
      setBroken(r.broken);
    } catch (e) { setMsg(e instanceof ApiError ? `扫描失败 ${e.status}` : String(e)); }
    finally { setBusy(false); }
  };

  const clearOne = async (b: BrokenRef) => {
    try {
      await api.post("/api/admin/orphans/broken/clear", { table: b.table, id: b.id, field: b.field });
      setBroken((prev) => prev?.filter((x) => !(x.table === b.table && x.id === b.id && x.field === b.field)) ?? null);
      setOkMsg("已置空该字段（前台不再引用缺失图）。");
    } catch (e) { setMsg(e instanceof ApiError ? `处理失败 ${e.status}` : String(e)); }
  };

  // 一键置空所有可修复（scalar）的失效引用；不可修复的（富文本/画廊）保留
  const clearAll = async () => {
    setConfirmAll(false);
    setBusy(true); setMsg(null);
    const targets = (broken ?? []).filter((b) => b.fixable);
    let done = 0;
    for (const b of targets) {
      try { await api.post("/api/admin/orphans/broken/clear", { table: b.table, id: b.id, field: b.field }); done++; }
      catch { /* 单条失败跳过 */ }
    }
    setBroken((prev) => prev?.filter((x) => !x.fixable) ?? null); // 移除已处理的可修复项
    setBusy(false);
    setOkMsg(`已置空 ${done} 处失效引用${targets.length > done ? `（${targets.length - done} 处失败）` : ""}。`);
  };

  return (
    <div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        检查数据库里引用了、但对象存储中已不存在的图片（前台会显示裂图），以及文件已丢失的备份记录。可一键把对应字段置空；富文本/画廊内的失效图需进对应编辑页手动移除；失效的备份记录请到「网站备份」页删除整条。
      </div>
      <button className="btn btn-primary" disabled={busy} onClick={scan} style={{ marginBottom: 16 }}>
        {busy ? "扫描中…" : "开始扫描"}
      </button>

      {msg && <div className="muted" style={{ marginBottom: 12 }}>{msg}</div>}

      {broken && (
        <div className="form">
          {broken.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>没有失效引用，所有引用的图片都存在。</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  共 <b style={{ color: "#b04a3a" }}>{broken.length}</b> 处失效引用{fixableCount > 0 && `，其中 ${fixableCount} 处可一键置空`}：
                </span>
                {fixableCount > 1 && (
                  <button className="btn" style={{ borderColor: "#b04a3a", color: "#b04a3a" }}
                    disabled={busy} onClick={() => setConfirmAll(true)}>置空所有（{fixableCount}）</button>
                )}
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {broken.map((b, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, border: "1px solid var(--border)", padding: "8px 12px" }}>
                    <span style={{ flex: 1 }}>
                      <b>{b.label}</b>
                      <span className="muted" style={{ marginLeft: 8, fontFamily: "monospace", wordBreak: "break-all" }}>{b.key}</span>
                    </span>
                    {b.fixable
                      ? <button className="btn btn-xs" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => clearOne(b)}>置空字段</button>
                      : <span className="muted" style={{ whiteSpace: "nowrap" }}>需手动编辑</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmAll}
        title="置空所有失效引用"
        message={<>确认把 {fixableCount} 处可修复的失效引用字段全部置空？前台将不再引用这些缺失图（可修复=封面/Logo 等单图字段；富文本/画廊内的不受影响，需手动处理）。</>}
        confirmLabel="全部置空"
        danger
        onConfirm={clearAll}
        onCancel={() => setConfirmAll(false)}
      />
      <AlertDialog open={okMsg !== null} title="已处理" message={okMsg} onClose={() => setOkMsg(null)} />
    </div>
  );
}

// ============ 清理文件 Tab（孤立文件扫描/删除）============

type Orphan = { key: string; size: number; lastModified: string | null };
type ScanResult = {
  orphans: Orphan[];
  summary: { objects_total: number; referenced_total: number; orphans_count: number; orphans_size: number };
};

function fmtSize(n: number) {
  if (!n || n < 1024) return `${n || 0} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}
function fmtTime(s: string | null) { return s ? new Date(s).toLocaleString("zh-CN", { hour12: false }) : "-"; }

function CleanupFilesTab() {
  const [result, setResult] = useState<ScanResult | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  // div 列表：传 rowSelector 让 hook 实测真实行高(含 gap)与起点，避免溢出滚动
  const { containerRef, pageSize } = useAutoPageSize({ fallbackRow: 44, min: 5, rowSelector: ".mnt-orphan-row" });

  const scan = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.get<ScanResult>("/api/admin/orphans/scan");
      setResult(r);
      setSel(new Set(r.orphans.map((o) => o.key))); // 默认全选
      setPage(1);
    } catch (e) { setMsg(e instanceof ApiError ? `扫描失败 ${e.status}` : String(e)); }
    finally { setBusy(false); }
  };

  const toggle = (key: string) => {
    const n = new Set(sel); n.has(key) ? n.delete(key) : n.add(key); setSel(n);
  };
  const toggleAll = () => {
    if (!result) return;
    setSel(sel.size === result.orphans.length ? new Set() : new Set(result.orphans.map((o) => o.key)));
  };

  const doDelete = async () => {
    setConfirmDel(false);
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<{ deleted: number; kept: number }>("/api/admin/orphans/delete", { keys: [...sel] });
      const parts = [`已删除 ${r.deleted} 个文件`];
      if (r.kept > 0) parts.push(`${r.kept} 个因仍被引用而保留`);
      setOkMsg(parts.join("，") + "。");
      await scan();
    } catch (e) { setMsg(e instanceof ApiError ? `删除失败 ${e.status}` : String(e)); }
    finally { setBusy(false); }
  };

  const selSize = result ? result.orphans.filter((o) => sel.has(o.key)).reduce((s, o) => s + o.size, 0) : 0;
  const pageItems = result ? result.orphans.slice((page - 1) * pageSize, page * pageSize) : [];

  return (
    <div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        扫描对象存储里数据库已无任何引用的文件并清理，包括无备份记录引用的孤儿备份 zip。删除时会再次复查，不会误删被引用的文件或有效备份。
      </div>
      <button className="btn btn-primary" disabled={busy} onClick={scan} style={{ marginBottom: 16 }}>
        {busy ? "处理中…" : "开始扫描"}
      </button>

      {msg && <div className="muted" style={{ marginBottom: 12 }}>{msg}</div>}

      {result && (
        <div className="form">
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            对象存储共 {result.summary.objects_total} 个文件，被引用 {result.summary.referenced_total} 个，
            <b style={{ color: "var(--gold)" }}> 失效 {result.summary.orphans_count} 个</b>
            （{fmtSize(result.summary.orphans_size)}）。
          </div>

          {result.orphans.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>没有失效文件，对象存储很干净。</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={sel.size === result.orphans.length} onChange={toggleAll} />
                  全选 / 取消（已选 {sel.size} 个，{fmtSize(selSize)}）
                </label>
                <button className="btn" style={{ borderColor: "#b04a3a", color: "#b04a3a" }}
                  disabled={busy || sel.size === 0} onClick={() => setConfirmDel(true)}>
                  删除选中
                </button>
              </div>

              <div ref={containerRef}>
                <div style={{ display: "grid", gap: 6 }}>
                  {pageItems.map((o) => (
                    <label key={o.key} className="mnt-orphan-row" style={{
                      display: "flex", alignItems: "center", gap: 10, fontSize: 12,
                      border: "1px solid var(--border)", padding: "8px 12px", cursor: "pointer",
                    }}>
                      <input type="checkbox" checked={sel.has(o.key)} onChange={() => toggle(o.key)} />
                      <span style={{ flex: 1, fontFamily: "monospace", wordBreak: "break-all" }}>{o.key}</span>
                      <span className="muted" style={{ whiteSpace: "nowrap" }}>{fmtSize(o.size)}</span>
                      <span className="muted" style={{ whiteSpace: "nowrap" }}>{fmtTime(o.lastModified)}</span>
                    </label>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Pager page={page} pageSize={pageSize} total={result.orphans.length} onChange={setPage} />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmDel}
        title="删除失效文件"
        message={<>确认删除选中的 {sel.size} 个文件（{fmtSize(selSize)}）？此操作不可撤销。删除时会再次复查，仍被引用的将自动保留。</>}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(false)}
      />
      <AlertDialog open={okMsg !== null} title="清理完成" message={okMsg} onClose={() => setOkMsg(null)} />
    </div>
  );
}
